import React from 'react';
import Link from 'next/link';
import _ from 'lodash';

export default function BetterLink(props) {
  const { href, route, children, scroll = true, prefetch, ...urlProps } = props;

  // Detect if any direct child is an anchor (including styled-components anchored to 'a')
  const childrenArray = React.Children.toArray(children);
  const hasAnchorChild = childrenArray.some((child) => {
    if (!React.isValidElement(child)) {
      return false;
    }
    const { type } = child;
    // Treat styled-components anchors and native 'a' as anchors
    return (_.isString(type) && type === 'a') || (type && type.target === 'a');
  });

  const linkProps = {
    prefetch,
    scroll,
    legacyBehavior: true,
    passHref: true,
  };

  // Prefer a concrete href string when provided. Fallback to route + query object.
  // This avoids broken dynamic routes like "/dispensary/[cName]" under Next.js 13
  // when no query is supplied.
  const linkHref = (() => {
    // If a concrete href string is provided, use it directly
    if (_.isString(href) && href.length > 0) {
      return href;
    }

    // If a dynamic route is provided, use pathname with query if available
    if (route) {
      const { query } = urlProps || {};
      return query ? { pathname: route, query } : route;
    }

    // Fallback to href as-is
    return href;
  })();

  // Collect anchor attributes that should be forwarded to the injected <a> when
  // children are not already an anchor. This preserves testids, classes, events, etc.
  const anchorAttrKeys = Object.keys(urlProps).filter(
    (key) =>
      key === 'className' ||
      key === 'id' ||
      key === 'title' ||
      key === 'role' ||
      key === 'tabIndex' ||
      key === 'target' ||
      key === 'rel' ||
      key === 'download' ||
      key === 'draggable' ||
      key === 'style' ||
      key.startsWith('data-') ||
      key.startsWith('aria-') ||
      key.startsWith('on') // event handlers like onClick
  );
  const forwardedAnchorAttrs = _.pick(urlProps, anchorAttrKeys);

  // If child is already an anchor, use it directly as the child of Next's Link (no injected <a>)
  if (hasAnchorChild) {
    // Filter out non-element children (like JSX comments) to avoid multiple children error
    const validChildren = React.Children.toArray(children).filter((child) => React.isValidElement(child));

    // Next.js Link only supports a single child, so if we have one valid child, use it directly
    // Otherwise wrap multiple children in a fragment (though this shouldn't happen in practice)
    if (validChildren.length === 1) {
      const child = validChildren[0];
      const extra = _.isString(linkHref) || _.isObject(linkHref) ? { href: linkHref } : {};
      return (
        <Link href={linkHref} {...linkProps}>
          {React.cloneElement(child, { ...extra, ...forwardedAnchorAttrs })}
        </Link>
      );
    }

    // Fallback for multiple children - wrap in a single anchor
    return (
      <Link href={linkHref} {...linkProps}>
        <a href={linkHref} {...forwardedAnchorAttrs}>
          {validChildren}
        </a>
      </Link>
    );
  }

  return (
    <Link href={linkHref} scroll={scroll} prefetch={prefetch} {...forwardedAnchorAttrs}>
      {children}
    </Link>
  );
}
